import React, { useEffect, useState } from "react";
import Footer from "components/home/Footer";
import NavBar from "components/home/Navbar";
import QuickInquiryModal from "components/home/QuickInquiryModal";
import { footerSections } from "components/home/homeData";

export default function PrivacyPolicy() {

    useEffect(() => {
        const html = document.documentElement;
        const body = document.body;
    
        html.style.overflow = "auto";
        body.style.overflow = "auto";
    
        html.style.overscrollBehavior = "auto";
        body.style.overscrollBehavior = "auto";
    
        return () => {
          html.style.overflow = "";
          body.style.overflow = "";
    
          html.style.overscrollBehavior = "";
          body.style.overscrollBehavior = "";
        };
      }, []);

    const [isQuickInquiryOpen, setIsQuickInquiryOpen] = useState(false);
    const [quickInquiryPosition, setQuickInquiryPosition] = useState<{ top: number; left: number } | null>(null);

    const openQuickInquiry = (event: React.MouseEvent<HTMLButtonElement>) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const panelWidth = 360;
        const panelHeight = 430;
        const viewportPadding = 12;

        let left = rect.right + 10;
        if (left + panelWidth > window.innerWidth - viewportPadding) {
            left = rect.left - panelWidth - 10;
        }
        left = Math.max(viewportPadding, Math.min(left, window.innerWidth - panelWidth - viewportPadding));

        let top = rect.top - panelHeight - 10;
        top = Math.max(viewportPadding, Math.min(top, window.innerHeight - panelHeight - viewportPadding));

        setQuickInquiryPosition({ top, left });
        setIsQuickInquiryOpen(true);
    };

    return (
        <div className="min-h-screen bg-white pt-10 font-sans">
            <NavBar signInHref="/login" signUpHref="/registration" />

            <section className="bg-gradient-to-br from-blue-50 via-blue-100 to-indigo-100 py-10">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-white rounded-2xl border border-[#dbe4f2] shadow-[0_16px_35px_rgba(17,46,96,0.08)] p-6 sm:p-8">
                        <h1 className="text-4xl sm:text-5xl font-bold text-[#0f1b35] mb-2">Privacy Policy</h1>
                        <p className="text-base text-[#5a6b89] mb-8">Effective date: May 7, 2026</p>

                        <div className="space-y-7 text-[#334b73]">
                            <section>
                                <h2 className="text-2xl font-semibold text-[#0f1b35] mb-2">1. Introduction</h2>
                                <p className="text-base leading-8">
                                    Liriworks respects your privacy and is committed to protecting your personal data. This policy explains
                                    what information we collect, how we use it, and the choices available to you when using our website and services.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-semibold text-[#0f1b35] mb-2">2. Information We Collect</h2>
                                <ul className="list-disc pl-5 text-base leading-8 space-y-1">
                                    <li>Contact information such as name, email address, phone number, and organization name.</li>
                                    <li>Account and profile details provided during registration or usage.</li>
                                    <li>Technical data such as IP address, browser type, device details, and usage analytics.</li>
                                    <li>Communication data when you contact support or submit forms like Quick Inquiry.</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-semibold text-[#0f1b35] mb-2">3. How We Use Your Information</h2>
                                <ul className="list-disc pl-5 text-base leading-8 space-y-1">
                                    <li>To provide, maintain, and improve the platform and related services.</li>
                                    <li>To respond to inquiries, support requests, and product demonstration requests.</li>
                                    <li>To send service notices, updates, and important account-related communications.</li>
                                    <li>To analyze performance, prevent abuse, and maintain security of our systems.</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-semibold text-[#0f1b35] mb-2">4. Legal Basis and Consent</h2>
                                <p className="text-base leading-8">
                                    We process personal data on lawful grounds, including consent, contractual necessity, legal obligations,
                                    and legitimate business interests, depending on the purpose of processing.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-semibold text-[#0f1b35] mb-2">5. Sharing and Disclosure</h2>
                                <p className="text-base leading-8">
                                    We do not sell personal data. We may share information with trusted service providers, legal authorities
                                    when required, and during business transfers, always with appropriate safeguards.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-semibold text-[#0f1b35] mb-2">6. Data Retention</h2>
                                <p className="text-base leading-8">
                                    Personal information is retained only as long as necessary for the purposes described in this policy,
                                    unless a longer retention period is required by law.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-semibold text-[#0f1b35] mb-2">7. Data Security</h2>
                                <p className="text-base leading-8">
                                    We implement administrative, technical, and organizational security controls to protect personal data
                                    against unauthorized access, disclosure, alteration, or destruction.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-semibold text-[#0f1b35] mb-2">8. Your Privacy Rights</h2>
                                <p className="text-base leading-8">
                                    Subject to local laws, you may request access, correction, deletion, restriction, or portability of your data,
                                    and may object to certain processing activities.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-semibold text-[#0f1b35] mb-2">9. Cookies and Tracking</h2>
                                <p className="text-base leading-8">
                                    We may use cookies and similar technologies to improve user experience, remember preferences,
                                    and gather analytics for platform improvement.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-semibold text-[#0f1b35] mb-2">10. Contact Us</h2>
                                <p className="text-base leading-8">
                                    For privacy-related questions or requests, please contact us at:
                                    <br />
                                    1660, 18th Cross Rd, MC Layout, Vijayanagar, Bengaluru
                                    <br />
                                    <a className="text-blue-500 underline" href="mailto:support.india@lirisoft.com">support.india@lirisoft.com</a>
                                </p>
                            </section>
                        </div>
                    </div>
                </div>
            </section>

            <Footer sections={footerSections} onQuickInquiryClick={openQuickInquiry} />
            <QuickInquiryModal
                isOpen={isQuickInquiryOpen}
                position={quickInquiryPosition}
                onClose={() => setIsQuickInquiryOpen(false)}
            />
        </div>
    );
}
